import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface ProdUser {
  id: string;
  name: string;
  role: 'field' | 'transport' | 'administration' | 'admin' | 'super_admin';
  centreId?: string;
}

interface ProdAuthContextValue {
  user: ProdUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const ProdAuthContext = createContext<ProdAuthContextValue | undefined>(undefined);

export const useProdAuth = () => {
  const ctx = useContext(ProdAuthContext);
  if (!ctx) throw new Error('useProdAuth must be used within ProdAuthProvider');
  return ctx;
};

export const ProdAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<ProdUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/production/auth?action=me', { credentials: 'include' })
      .then(async res => {
        if (res.ok) {
          const data = await res.json();
          setUser(data);
        } else {
          setUser(null);
        }
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch('/api/production/auth?action=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        return { success: true };
      }
      const data = await res.json().catch(() => ({}));
      return { success: false, error: data.error || 'Invalid credentials' };
    } catch {
      return { success: false, error: 'Network error' };
    }
  }, []);

  const logout = useCallback(async () => {
    await fetch('/api/production/auth?action=logout', {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
    setUser(null);
  }, []);

  return (
    <ProdAuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </ProdAuthContext.Provider>
  );
};
